import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import CompanyIdentityStep from "@/components/CompanyIdentityStep";
import { ReqLabel } from "@/components/DiscoveryFormParts";
import QuestionnaireStepper from "@/components/QuestionnaireStepper";
import MediaRequestBox from "@/components/MediaRequestBox";
import api, { formatApiError, uploadFileChunked } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, ArrowRight, ArrowLeft, Building2, Briefcase, BarChart3, Trophy, ImageIcon, FileText, Check, UploadCloud, X, Loader2, Film } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  siret: "", legal_form: "", address: "", postal_code: "", naf_code: "", company_info: null, net_result: "",
  title: "", sector: "", region: "", city: "", year_founded: "",
  activity: "", clients: "", market: "",
  revenue: "", ebitda: "", employees: "", profitability_rate: "", asking_price: "", asking_price_range: "",
  strengths: "", growth: "", reason_for_sale: "",
  hero_image: "", video_url: "", gallery: "", lat: 46.6, lng: 2.4,
  short_description: "", full_description: "",
};

const STEPS = [
  { icon: Building2, short: "Identité", title: "Identité de l'entreprise", desc: "Saisissez votre SIRET : la fiche se remplit automatiquement.", fields: ["siret", "title", "sector", "region", "city", "year_founded"] },
  { icon: Briefcase, short: "Activité", title: "Activité & marché", desc: "Décrivez votre activité, vos clients et votre positionnement.", fields: ["activity", "clients", "market"] },
  { icon: BarChart3, short: "Chiffres", title: "Chiffres clés", desc: "Les principaux indicateurs financiers.", fields: ["revenue", "ebitda", "net_result", "employees", "asking_price"] },
  { icon: Trophy, short: "Atouts", title: "Atouts & perspectives", desc: "Ce qui rend votre entreprise attractive.", fields: ["strengths", "growth", "reason_for_sale"] },
  { icon: ImageIcon, short: "Médias", title: "Médias & localisation", desc: "Photos, vidéo et emplacement sur la carte.", fields: [] },
  { icon: FileText, short: "Présentation", title: "Présentation générée", desc: "L'IA rédige votre présentation à partir de vos réponses.", fields: ["short_description", "full_description"] },
];
const DRAFT_KEY = "proac_seller_draft";
const filled = (v) => v !== null && v !== undefined && String(v).trim() !== "";
const loadDraft = (key) => { try { return JSON.parse(localStorage.getItem(key)) || null; } catch { return null; } };

export default function SellerQuestionnaire({ open, onOpenChange, onSubmitted, submitUrl = "/seller/businesses", ownerId, initial, successMessage }) {
  const { user } = useAuth();
  const draftKey = `${DRAFT_KEY}_${ownerId || user?.user_id || "anon"}`;
  const draft = loadDraft(draftKey);
  const [step, setStep] = useState(draft?.step || 0);
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}), ...(draft?.form || {}) });
  const [reference, setReference] = useState({ sectors: [], regions: [] });
  const [mediaRequested, setMediaRequested] = useState(false);
  const [showErr, setShowErr] = useState(false);
  const isSeller = user?.role === "seller";
  useEffect(() => {
    if (draft || (!isSeller && !ownerId)) return;
    api.get(isSeller ? "/seller/prefill" : `/admin/onsite-sellers/${ownerId}/prefill`).then(({ data }) => setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(data).filter(([, v]) => v !== "" && v != null)) }))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { api.get("/meta/reference").then((r) => setReference(r.data)).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem(draftKey, JSON.stringify({ form, step })); }, [form, step, draftKey]);

  const stepDone = STEPS.map((st, i) => (i === 4 ? !!form.hero_image || mediaRequested : st.fields.filter((k) => filled(form[k])).length >= Math.ceil(st.fields.length / 2)));
  const allFields = STEPS.flatMap((st) => st.fields);
  const completion = Math.round((allFields.filter((k) => filled(form[k])).length / allFields.length) * 100);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState("");
  const [progress, setProgress] = useState(0);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const galleryList = form.gallery ? form.gallery.split(",").map((s) => s.trim()).filter(Boolean) : [];

  const handleUpload = async (kind, files) => {
    if (!files || files.length === 0) return;
    setUploading(kind);
    setProgress(0);
    try {
      if (kind === "gallery") {
        const urls = [];
        for (const f of Array.from(files)) urls.push(await uploadFileChunked(f, setProgress));
        setForm((prev) => ({ ...prev, gallery: [...galleryList, ...urls].join(", ") }));
      } else {
        const url = await uploadFileChunked(files[0], setProgress);
        setForm((prev) => ({ ...prev, [kind === "hero" ? "hero_image" : "video_url"]: url }));
      }
      toast.success("Fichier téléversé.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Le téléversement a échoué.");
    } finally {
      setUploading("");
      setProgress(0);
    }
  };

  const removeGallery = (url) => setForm((prev) => ({ ...prev, gallery: galleryList.filter((u) => u !== url).join(", ") }));

  const reset = () => { localStorage.removeItem(draftKey); setForm(EMPTY); setStep(0); };
  const close = (v) => onOpenChange(v);

  const missingIn = (i) => {
    const m = [];
    if (i === 0) {
      if (!form.title?.trim()) m.push(["title", "Nom / raison sociale"]);
      if (!form.sector) m.push(["sector", "Secteur d'activité"]);
      if (!form.region) m.push(["region", "Région"]);
      if (!form.city?.trim()) m.push(["city", "Ville"]);
    }
    if (i === 1 && (form.activity || "").trim().length <= 5) m.push(["activity", "Description de l'activité"]);
    if (i === 4 && isSeller && !form.hero_image && !mediaRequested) m.push(["hero_image", "Image de couverture (ou demande de visuels à PROAC)"]);
    if (i === 5 && (!form.short_description?.trim() || !form.full_description?.trim())) m.push(["presentation", "Présentation (cliquez sur « Générer ma présentation »)"]);
    return m;
  };
  const canNext = () => missingIn(step).length === 0;
  const badKeys = showErr ? new Set(STEPS.flatMap((_, i) => missingIn(i).map(([k]) => k))) : new Set();
  const errMsg = (k) => (k === "activity" ? "Champ obligatoire (au moins quelques mots)." : "Champ obligatoire.");
  const warnMissing = (items) => {
    setShowErr(true);
    toast.error(`Champ(s) obligatoire(s) manquant(s) : ${items.map(([, l]) => l).join(", ")}`);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="q-field-${items[0][0]}"], [data-testid="q-select-${items[0][0]}"], [data-testid="q-err-${items[0][0]}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/seller/generate-presentation", {
        title: form.title, sector: form.sector, region: form.region, city: form.city,
        year_founded: form.year_founded === "" ? null : parseInt(form.year_founded),
        activity: form.activity, clients: form.clients, market: form.market,
        strengths: form.strengths, growth: form.growth, reason_for_sale: form.reason_for_sale,
        revenue: form.revenue === "" ? null : parseFloat(form.revenue),
        ebitda: form.ebitda === "" ? null : parseFloat(form.ebitda),
        employees: form.employees === "" ? null : parseInt(form.employees),
        profitability_rate: form.profitability_rate === "" ? null : parseFloat(form.profitability_rate),
      });
      setForm((f) => ({ ...f, short_description: data.short_description, full_description: data.full_description }));
      toast.success("Présentation générée. Vous pouvez la relire et l'ajuster.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const submit = async () => {
    const firstBad = STEPS.findIndex((_, i) => missingIn(i).length > 0);
    if (firstBad !== -1) {
      warnMissing(missingIn(firstBad));
      setStep(firstBad);
      return;
    }
    setSubmitting(true);
    try {
      await api.post(submitUrl, {
        title: form.title, sector: form.sector, region: form.region, city: form.city,
        profitability_rate: parseFloat(form.profitability_rate) || 0,
        asking_price_range: form.asking_price_range || "Sur demande",
        short_description: form.short_description,
        hero_image: form.hero_image || "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        lat: parseFloat(form.lat) || 46.6, lng: parseFloat(form.lng) || 2.4,
        full_description: form.full_description, video_url: form.video_url,
        revenue: form.revenue === "" ? null : parseFloat(form.revenue),
        ebitda: form.ebitda === "" ? null : parseFloat(form.ebitda),
        employees: form.employees === "" ? null : parseInt(form.employees),
        net_result: form.net_result === "" || form.net_result == null ? null : parseFloat(form.net_result),
        year_founded: form.year_founded === "" ? null : parseInt(form.year_founded),
        asking_price: form.asking_price === "" ? null : parseFloat(form.asking_price),
        gallery: form.gallery ? form.gallery.split(",").map((s) => s.trim()).filter(Boolean) : [],
        advisor_name: "PROAC Transmission", advisor_email: "contact@proac-transmission.fr", advisor_phone: "06 61 16 10 38",
        questionnaire: {
          activity: form.activity, clients: form.clients, market: form.market,
          strengths: form.strengths, growth: form.growth, reason_for_sale: form.reason_for_sale,
          company: { siret: form.siret, name: form.title, legal_form: form.legal_form, address: form.address, naf_code: form.naf_code, official: form.company_info },
        },
      });
      toast.success(successMessage || "Fiche transmise à PROAC Transmission — elle reste privée jusqu'à validation de sa publication.");
      reset();
      close(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const errCls = (k) => (badKeys.has(k) ? "border-red-500 ring-1 ring-red-500" : "border-gray-300");
  const errText = (k) => (badKeys.has(k) ? <p data-testid={`q-err-${k}`} className="text-xs text-red-600 mt-1">{errMsg(k)}</p> : null);
  const Fld = ({ k, label, type = "text", placeholder }) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569]"><ReqLabel text={label} /></Label>
      <Input type={type} step={type === "number" ? "any" : undefined} value={form[k] ?? ""} onChange={set(k)} placeholder={placeholder} aria-invalid={badKeys.has(k)} className={`rounded-none mt-1.5 ${errCls(k)}`} data-testid={`q-field-${k}`} />
      {errText(k)}
    </div>
  );
  const Area = ({ k, label, placeholder }) => (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#475569]"><ReqLabel text={label} /></Label>
      <Textarea value={form[k] ?? ""} onChange={set(k)} placeholder={placeholder} aria-invalid={badKeys.has(k)} className={`rounded-none mt-1.5 min-h-[90px] ${errCls(k)}`} data-testid={`q-field-${k}`} />
      {errText(k)}
    </div>
  );

  const S = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="seller-questionnaire">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#1B2A4A] flex items-center justify-center shrink-0"><S.icon className="w-5 h-5 text-[#E0C173]" strokeWidth={1.5} /></div>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#E0C173]">Étape {step + 1} / {STEPS.length}</p>
              <DialogTitle className="font-display text-xl text-[#1B2A4A]">{S.title}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-[#475569] mt-2">{S.desc}</DialogDescription>
        </DialogHeader>

        <QuestionnaireStepper steps={STEPS} current={step} done={stepDone} completion={completion} onGo={(i) => (i <= step || canNext() ? setStep(i) : warnMissing(missingIn(step)))} />

        <div className="py-2 space-y-4">
          {step === 0 && <CompanyIdentityStep form={form} setForm={setForm} set={set} reference={reference} Fld={Fld} badKeys={badKeys} />}
          {step === 1 && (
            <>
              {Area({ k: "activity", label: "Décrivez votre activité *", placeholder: "Que fait votre entreprise au quotidien, ses produits/services…" })}
              {Area({ k: "clients", label: "Votre clientèle", placeholder: "Type de clients, récurrence, fidélité, contrats…" })}
              {Area({ k: "market", label: "Positionnement & marché", placeholder: "Vos concurrents, votre emplacement, vos avantages sur le marché…" })}
            </>
          )}
          {step === 2 && (
            <div className="grid sm:grid-cols-2 gap-4">
              {Fld({ k: "revenue", label: "Chiffre d'affaires (€)", type: "number", placeholder: "720000" })}
              {Fld({ k: "ebitda", label: "EBE / EBITDA (€)", type: "number", placeholder: "133000" })}
              {Fld({ k: "net_result", label: "Résultat net (€)", type: "number", placeholder: "90000" })}
              {Fld({ k: "profitability_rate", label: "Taux de rentabilité (%)", type: "number", placeholder: "18.5" })}
              {Fld({ k: "employees", label: "Effectif (salariés)", type: "number", placeholder: "6" })}
              {Fld({ k: "asking_price", label: "Prix de cession souhaité (€)", type: "number", placeholder: "410000" })}
              {Fld({ k: "asking_price_range", label: "Fourchette affichée (public)", placeholder: "350 K€ – 450 K€" })}
            </div>
          )}
          {step === 3 && (
            <>
              {Area({ k: "strengths", label: "Vos atouts / points forts", placeholder: "Équipe, emplacement, équipements, réputation, savoir-faire…" })}
              {Area({ k: "growth", label: "Perspectives de développement", placeholder: "Pistes de croissance pour un repreneur…" })}
              {Area({ k: "reason_for_sale", label: "Raison de la cession", placeholder: "Ex. départ à la retraite, nouveau projet…" })}
            </>
          )}
          {step === 4 && (
            <div className="space-y-5">
              {isSeller && <MediaRequestBox businessTitle={form.title} onRequested={() => setMediaRequested(true)} />}
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Image de couverture {isSeller && !mediaRequested && <span className="text-red-500">*</span>}</Label>
                <div className="mt-2 flex items-center gap-4">
                  {form.hero_image ? (
                    <div className="relative w-32 h-20 border border-gray-200 overflow-hidden shrink-0">
                      <img src={form.hero_image} alt="aperçu" className="w-full h-full object-cover" />
                      <button onClick={() => setForm((f) => ({ ...f, hero_image: "" }))} className="absolute top-0.5 right-0.5 bg-black/60 text-white p-0.5" data-testid="q-hero-remove"><X className="w-3 h-3" /></button>
                    </div>
                  ) : null}
                  <label className={`cursor-pointer inline-flex items-center gap-2 border border-dashed px-4 py-3 text-sm text-[#475569] hover:border-[#E0C173] ${badKeys.has("hero_image") ? "border-red-500 bg-red-50" : "border-gray-300"}`} data-testid="q-hero-upload">
                    {uploading === "hero" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    {uploading === "hero" ? `Téléversement ${progress}%` : (form.hero_image ? "Remplacer l'image" : "Téléverser une image")}
                    <input type="file" accept="image/*" className="hidden" disabled={!!uploading} onChange={(e) => handleUpload("hero", e.target.files)} />
                  </label>
                </div>
                {badKeys.has("hero_image") && <p data-testid="q-err-hero_image" className="text-xs text-red-600 mt-1">Ajoutez une image de couverture ou demandez des visuels à PROAC ci-dessus.</p>}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Vidéo de présentation (facultatif)</Label>
                <div className="mt-2 flex items-center gap-4">
                  {form.video_url ? (
                    <span className="inline-flex items-center gap-1.5 text-sm text-[#1B2A4A] bg-[#F8F7F4] border border-gray-200 px-3 py-2"><Film className="w-4 h-4 text-[#C9A85A]" /> Vidéo ajoutée <button onClick={() => setForm((f) => ({ ...f, video_url: "" }))} data-testid="q-video-remove"><X className="w-3 h-3 ml-1" /></button></span>
                  ) : null}
                  <label className="cursor-pointer inline-flex items-center gap-2 border border-dashed border-gray-300 px-4 py-3 text-sm text-[#475569] hover:border-[#E0C173]" data-testid="q-video-upload">
                    {uploading === "video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    {uploading === "video" ? `Téléversement ${progress}%` : (form.video_url ? "Remplacer la vidéo" : "Téléverser une vidéo (MP4)")}
                    <input type="file" accept="video/*" className="hidden" disabled={!!uploading} onChange={(e) => handleUpload("video", e.target.files)} />
                  </label>
                </div>
                <p className="mt-1.5 text-xs text-gray-400">Vous pouvez aussi coller un lien YouTube/Vimeo ci-dessous.</p>
                <Input value={form.video_url} onChange={set("video_url")} placeholder="https://youtube.com/…" className="rounded-none mt-1.5 border-gray-300" data-testid="q-field-video_url" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-[#475569]">Galerie photos</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {galleryList.map((u) => (
                    <div key={u} className="relative w-20 h-16 border border-gray-200 overflow-hidden">
                      <img src={u} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeGallery(u)} className="absolute top-0.5 right-0.5 bg-black/60 text-white p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label className="cursor-pointer inline-flex items-center justify-center gap-2 border border-dashed border-gray-300 w-20 h-16 text-xs text-[#475569] hover:border-[#E0C173]" data-testid="q-gallery-upload">
                    {uploading === "gallery" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                    <input type="file" accept="image/*" multiple className="hidden" disabled={!!uploading} onChange={(e) => handleUpload("gallery", e.target.files)} />
                  </label>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {Fld({ k: "lat", label: "Latitude (carte)", type: "number", placeholder: "48.11" })}
                {Fld({ k: "lng", label: "Longitude (carte)", type: "number", placeholder: "-1.68" })}
              </div>
            </div>
          )}
          {step === 5 && (
            <div className="space-y-4">
              {!form.full_description ? (
                <div data-testid="q-generate-box" className={`text-center border border-dashed py-10 px-6 ${badKeys.has("presentation") ? "border-red-500 bg-red-50" : "border-gray-300 bg-[#F8F7F4]"}`}>
                  {badKeys.has("presentation") && <p data-testid="q-err-presentation" className="text-sm text-red-600 font-medium mb-3">La présentation est obligatoire : cliquez sur « Générer ma présentation ».</p>}
                  <Sparkles className="w-8 h-8 text-[#E0C173] mx-auto mb-3" />
                  <p className="text-sm text-[#475569] max-w-md mx-auto">À partir de vos réponses, notre IA rédige une présentation professionnelle et un teaser public anonymisé.</p>
                  <Button onClick={generate} disabled={generating} className="mt-5 rounded-full bg-[#1B2A4A] hover:bg-[#0D1629]" data-testid="q-generate-btn">
                    <Sparkles className="w-4 h-4 mr-2" /> {generating ? "Génération en cours…" : "Générer ma présentation"}
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs uppercase tracking-wider text-[#475569]">Teaser public (anonymisé)</Label>
                      <button onClick={generate} disabled={generating} className="text-xs text-[#C9A85A] hover:underline flex items-center gap-1" data-testid="q-regenerate-btn"><Sparkles className="w-3 h-3" /> {generating ? "…" : "Régénérer"}</button>
                    </div>
                    <Textarea value={form.short_description} onChange={set("short_description")} className="rounded-none mt-1.5 border-gray-300 min-h-[70px]" data-testid="q-short-desc" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#475569]">Présentation détaillée (réservée aux acheteurs)</Label>
                    <Textarea value={form.full_description} onChange={set("full_description")} className="rounded-none mt-1.5 border-gray-300 min-h-[220px]" data-testid="q-full-desc" />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <p data-testid="q-required-note" className="text-xs text-[#475569]">Les champs marqués d'un <span className="text-red-500">*</span> sont obligatoires.</p>
        <p data-testid="q-private-note" className="text-xs text-gray-500 flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-600" /> Brouillon enregistré automatiquement · Fiche privée, visible uniquement par PROAC Transmission jusqu'à sa validation.</p>
        <div className="flex items-center justify-between border-t border-gray-100 pt-4">
          <Button variant="ghost" className="rounded-none" onClick={() => (step === 0 ? close(false) : setStep((s) => s - 1))} data-testid="q-back-btn">
            {step === 0 ? "Enregistrer et fermer" : <><ArrowLeft className="w-4 h-4 mr-1" /> Précédent</>}
          </Button>
          {!isLast ? (
            <Button onClick={() => (canNext() ? (setShowErr(false), setStep((s) => s + 1)) : warnMissing(missingIn(step)))} className="rounded-none bg-[#1B2A4A] hover:bg-[#0D1629]" data-testid="q-next-btn">
              Suivant <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={submitting} className="rounded-none bg-[#E0C173] hover:bg-[#C9A85A] text-[#1B2A4A] font-semibold" data-testid="q-submit-btn">
              <Check className="w-4 h-4 mr-1" /> {submitting ? "Envoi…" : "Transmettre ma fiche à PROAC"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
